import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";

// Currency mapping for display
export const currencyMap: Record<string, { symbol: string; iso: string; name: string }> = {
  "$": { symbol: "$", iso: "USD", name: "US Dollar" },
  "€": { symbol: "€", iso: "EUR", name: "Euro" },
  "£": { symbol: "£", iso: "GBP", name: "British Pound" },
  "¥": { symbol: "¥", iso: "JPY", name: "Japanese Yen" },
  "CHF": { symbol: "CHF", iso: "CHF", name: "Swiss Franc" },
  "₣": { symbol: "₣", iso: "CHF", name: "Swiss Franc" },
  "₹": { symbol: "₹", iso: "INR", name: "Indian Rupee" },
  "د.ك": { symbol: "د.ك", iso: "KWD", name: "Kuwaiti Dinar" },
  "د.إ": { symbol: "د.إ", iso: "AED", name: "UAE Dirham" },
  "¢": { symbol: "¢", iso: "GHS", name: "Ghanaian Cedi" },
  "kr": { symbol: "kr", iso: "SEK", name: "Swedish Krona" },
  "": { symbol: "", iso: "IRR", name: "Iranian Rial" },
  "₩": { symbol: "₩", iso: "KRW", name: "South Korean Won" },
  "₻": { symbol: "₻", iso: "BGN", name: "Bulgarian Lev" },
  "₽": { symbol: "₽", iso: "RUB", name: "Russian Ruble" },
  "₾": { symbol: "₾", iso: "GEL", name: "Georgian Lari" },
  "₺": { symbol: "₺", iso: "TRY", name: "Turkish Lira" },
  "₼": { symbol: "₼", iso: "AZN", name: "Azerbaijani Manat" },
  "₸": { symbol: "₸", iso: "KZT", name: "Kazakhstani Tenge" },
  "₴": { symbol: "₴", iso: "UAH", name: "Ukrainian Hryvnia" },
  "₷": { symbol: "₷", iso: "CZK", name: "Czech Koruna" },
  "฿": { symbol: "฿", iso: "THB", name: "Thai Baht" },
  "원": { symbol: "원", iso: "KRW", name: "South Korean Won" },
  "₫": { symbol: "₫", iso: "VND", name: "Vietnamese Dong" },
  "₮": { symbol: "₮", iso: "MNT", name: "Mongolian Tugrik" },
  "₯": { symbol: "₯", iso: "GRD", name: "Greek Drachma" },
  "₱": { symbol: "₱", iso: "PHP", name: "Philippine Peso" },
  "₳": { symbol: "₳", iso: "AUD", name: "Australian Dollar" },
  "₵": { symbol: "₵", iso: "GHS", name: "Ghanaian Cedi" },
  "₲": { symbol: "₲", iso: "PYG", name: "Paraguayan Guarani" },
  "₪": { symbol: "₪", iso: "ILS", name: "Israeli Shekel" },
  "₰": { symbol: "₰", iso: "DKK", name: "Danish Krone" },
  "R": { symbol: "R", iso: "ZAR", name: "South African Rand" },
};

// Helper function to get currency display
export const getCurrencyDisplay = (currency: string, displayType: 'symbol' | 'iso'): string => {
  const currencyInfo = currencyMap[currency];
  if (!currencyInfo) return currency;
  return displayType === 'symbol' ? currencyInfo.symbol : currencyInfo.iso;
};

export interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

export interface InvoiceGeneratorState {
  // Logo
  logoDataUrl: string | null;
  
  // Invoice details
  invoiceNumber: string;
  date: string;
  currency: string;
  currencyDisplayType: 'symbol' | 'iso';
  
  // From/To information
  fromName: string;
  fromAddress: string;
  fromCity: string;
  fromState: string;
  fromZip: string;
  fromCountry: string;
  fromEmail: string;
  fromPhone: string;
  
  billToName: string;
  billToAddress: string;
  billToCity: string;
  billToState: string;
  billToZip: string;
  billToCountry: string;
  billToEmail: string;
  billToPhone: string;
  
  shipToName: string;
  shipToAddress: string;
  shipToCity: string;
  shipToState: string;
  shipToZip: string;
  shipToCountry: string;
  
  // Invoice options
  paymentTerms: string;
  dueDate: string;
  poNumber: string;
  
  // Items
  items: InvoiceItem[];
  
  // Calculations
  subtotal: number;
  taxPercentage: number;
  taxAmount: number;
  discountPercentage: number;
  discountAmount: number;
  shipping: number;
  total: number;
  amountPaid: number;
  balanceDue: number;
  
  // Additional information
  notes: string;
  terms: string;

  // UI state
  isGenerating: boolean;
  error: string | null;
  hasDownloaded: boolean;
}

const initialState: InvoiceGeneratorState = {
  logoDataUrl: null,
  
  invoiceNumber: "INV-001",
  date: new Date().toISOString().split('T')[0],
  currency: "$",
  currencyDisplayType: 'symbol',
  
  fromName: "",
  fromAddress: "",
  fromCity: "",
  fromState: "",
  fromZip: "",
  fromCountry: "",
  fromEmail: "",
  fromPhone: "",
  
  billToName: "",
  billToAddress: "",
  billToCity: "",
  billToState: "",
  billToZip: "",
  billToCountry: "",
  billToEmail: "",
  billToPhone: "",
  
  shipToName: "",
  shipToAddress: "",
  shipToCity: "",
  shipToState: "",
  shipToZip: "",
  shipToCountry: "",
  
  paymentTerms: "",
  dueDate: "",
  poNumber: "",
  
  items: [],
  
  subtotal: 0,
  taxPercentage: 0,
  taxAmount: 0,
  discountPercentage: 0,
  discountAmount: 0,
  shipping: 0,
  total: 0,
  amountPaid: 0,
  balanceDue: 0,
  
  notes: "",
  terms: "",
  
  isGenerating: false,
  error: null,
  hasDownloaded: false,
};

// Utility function to calculate invoice totals
const calculateTotals = (state: InvoiceGeneratorState): InvoiceGeneratorState => {
  const subtotal = state.items.reduce((sum, item) => sum + item.amount, 0);
  const taxAmount = (subtotal * state.taxPercentage) / 100;
  const discountAmount = (subtotal * state.discountPercentage) / 100;
  const total = subtotal + taxAmount - discountAmount + state.shipping;
  const balanceDue = total - state.amountPaid;
  
  return {
    ...state,
    subtotal,
    taxAmount,
    discountAmount,
    total,
    balanceDue,
  };
};

// Async thunk for PDF generation
export const generateInvoicePdfAsync = createAsyncThunk(
  "invoiceGenerator/generatePdf",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const invoiceState = state.invoiceGenerator;
      
      if (!invoiceState.fromName) {
        throw new Error("Please fill in the required fields (From information)");
      }
      
      if (invoiceState.items.length === 0) {
        throw new Error("Please add at least one item to the invoice");
      }
      
      // Import jsPDF dynamically
      const { jsPDF } = await import("jspdf");
      
      // Create PDF document
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      
      let currentY = margin;
      
      // Add title
      pdf.setFontSize(24);
      pdf.setFont("helvetica", "bold");
      pdf.text("INVOICE", pageWidth / 2, currentY, { align: "center" });
      currentY += 15;
      
      // Add logo if present
      if (invoiceState.logoDataUrl) {
        currentY -= 15;
        try {
          const img = new Image();
          img.src = invoiceState.logoDataUrl;
          
          await new Promise<void>((resolve, reject) => {
            img.onload = () => {
              const logoHeight = 30;
              const logoWidth =  (img.width * logoHeight) / img.height;
              
              pdf.addImage(
                invoiceState.logoDataUrl!,
                "JPEG",
                margin,
                currentY,
                logoWidth,
                logoHeight,
                undefined,
                "FAST"
              );
              
              currentY += 15;
              resolve();
            };
            img.onerror = reject;
          });
        } catch (error) {
          console.warn("Failed to load logo:", error);
        }
      }

      // Add invoice details
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");
      
      const rightColumnX = pageWidth - margin - 60;
      
      if (invoiceState.invoiceNumber) {
        pdf.text("Invoice #:", rightColumnX, currentY);
        pdf.text(invoiceState.invoiceNumber, rightColumnX + 30, currentY);
        currentY += 6;
      }
      
      if (invoiceState.date) {
        pdf.text("Date:", rightColumnX, currentY);
        pdf.text(invoiceState.date, rightColumnX + 30, currentY);
        currentY += 6;
      }
      
      if (invoiceState.dueDate) {
        pdf.text("Due Date:", rightColumnX, currentY);
        pdf.text(invoiceState.dueDate, rightColumnX + 30, currentY);
        currentY += 6;
      }
      
      if (invoiceState.poNumber) {
        pdf.text("PO #:", rightColumnX, currentY);
        pdf.text(invoiceState.poNumber, rightColumnX + 30, currentY);
        currentY += 6;
      }
      
      currentY += 10;
      
      // Add From information
      const fromX = margin;
      const fromY = currentY;
      
      pdf.setFont("helvetica", "bold");
      pdf.text("FROM:", fromX, fromY);
      pdf.setFont("helvetica", "normal");
      
      let fromTextY = fromY + 6;
      if (invoiceState.fromName) {
        pdf.text(invoiceState.fromName, fromX, fromTextY);
        fromTextY += 5;
      }
      if (invoiceState.fromAddress) {
        pdf.text(invoiceState.fromAddress, fromX, fromTextY);
        fromTextY += 5;
      }
      if (invoiceState.fromCity || invoiceState.fromState || invoiceState.fromZip) {
        const cityStateZip = [invoiceState.fromCity, invoiceState.fromState, invoiceState.fromZip].filter(Boolean).join(", ");
        pdf.text(cityStateZip, fromX, fromTextY);
        fromTextY += 5;
      }
      if (invoiceState.fromCountry) {
        pdf.text(invoiceState.fromCountry, fromX, fromTextY);
        fromTextY += 5;
      }
      if (invoiceState.fromEmail) {
        pdf.text(invoiceState.fromEmail, fromX, fromTextY);
        fromTextY += 5;
      }
      if (invoiceState.fromPhone) {
        pdf.text(invoiceState.fromPhone, fromX, fromTextY);
        fromTextY += 5;
      }
      
      // Add Bill To information
      const billToX = pageWidth / 2;
      const billToY = currentY;
      
      pdf.setFont("helvetica", "bold");
      pdf.text("BILL TO:", billToX, billToY);
      pdf.setFont("helvetica", "normal");
      
      let billToTextY = billToY + 6;
      if (invoiceState.billToName) {
        pdf.text(invoiceState.billToName, billToX, billToTextY);
        billToTextY += 5;
      }
      if (invoiceState.billToAddress) {
        pdf.text(invoiceState.billToAddress, billToX, billToTextY);
        billToTextY += 5;
      }
      if (invoiceState.billToCity || invoiceState.billToState || invoiceState.billToZip) {
        const cityStateZip = [invoiceState.billToCity, invoiceState.billToState, invoiceState.billToZip].filter(Boolean).join(", ");
        pdf.text(cityStateZip, billToX, billToTextY);
        billToTextY += 5;
      }
      if (invoiceState.billToCountry) {
        pdf.text(invoiceState.billToCountry, billToX, billToTextY);
        billToTextY += 5;
      }
      if (invoiceState.billToEmail) {
        pdf.text(invoiceState.billToEmail, billToX, billToTextY);
        billToTextY += 5;
      }
      if (invoiceState.billToPhone) {
        pdf.text(invoiceState.billToPhone, billToX, billToTextY);
        billToTextY += 5;
      }
      
      // Add Ship To information if different from Bill To
      const hasShipTo = invoiceState.shipToName || invoiceState.shipToAddress || invoiceState.shipToCity || 
                       invoiceState.shipToState || invoiceState.shipToZip || invoiceState.shipToCountry;
      
      if (hasShipTo) {
        const shipToX = rightColumnX;
        const shipToY = currentY;
        
        pdf.setFont("helvetica", "bold");
        pdf.text("SHIP TO:", shipToX, shipToY);
        pdf.setFont("helvetica", "normal");
        
        let shipToTextY = shipToY + 6;
        if (invoiceState.shipToName) {
          pdf.text(invoiceState.shipToName, shipToX, shipToTextY);
          shipToTextY += 5;
        }
        if (invoiceState.shipToAddress) {
          pdf.text(invoiceState.shipToAddress, shipToX, shipToTextY);
          shipToTextY += 5;
        }
        if (invoiceState.shipToCity || invoiceState.shipToState || invoiceState.shipToZip) {
          const cityStateZip = [invoiceState.shipToCity, invoiceState.shipToState, invoiceState.shipToZip].filter(Boolean).join(", ");
          pdf.text(cityStateZip, shipToX, shipToTextY);
          shipToTextY += 5;
        }
        if (invoiceState.shipToCountry) {
          pdf.text(invoiceState.shipToCountry, shipToX, shipToTextY);
          shipToTextY += 5;
        }
      }
      
      // Find the maximum Y position from the address sections
      const maxAddressY = Math.max(fromTextY, billToTextY, hasShipTo ? fromTextY + 30 : 0);
      currentY = maxAddressY + 15;
      
      // Add items table
      const tableStartY = currentY;
      const tableHeaders = ["Description", "Qty", "Rate", "Amount"];
      const columnWidths = [80, 20, 30, 35];
      const columnX = [margin, margin + 80, margin + 100, margin + 135];
      
      // Draw table headers
      pdf.setFont("helvetica", "bold");
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, tableStartY - 5, contentWidth, 8, "F");
      
      tableHeaders.forEach((header, index) => {
        if (index === 3) { // Amount column - right align
          pdf.text(header, columnX[index] + columnWidths[index], tableStartY, { align: "right" });
        } else {
          pdf.text(header, columnX[index], tableStartY);
        }
      });
      
      currentY = tableStartY + 10;
      
      // Add items
      pdf.setFont("helvetica", "normal");
      invoiceState.items.forEach((item) => {
        // Check if we need a new page
        if (currentY > pageHeight - 80) {
          pdf.addPage();
          currentY = margin + 20;
        }
        
        pdf.text(item.description, columnX[0], currentY);
        pdf.text(item.quantity.toString(), columnX[1], currentY);
        pdf.text(`${getCurrencyDisplay(invoiceState.currency, invoiceState.currencyDisplayType)} ${item.rate.toFixed(2)}`, columnX[2], currentY);
        pdf.text(`${getCurrencyDisplay(invoiceState.currency, invoiceState.currencyDisplayType)} ${item.amount.toFixed(2)}`, columnX[3] + columnWidths[3], currentY, { align: "right" });
        
        currentY += 6;
      });
      
      currentY += 10;
      
      // Add totals
      const totalsX = pageWidth - margin - 60;
      const amountColumnX = columnX[3] + columnWidths[3]; // Align with items table Amount column
      
      pdf.setFont("helvetica", "normal");
      pdf.text("Subtotal:", totalsX, currentY);
      pdf.text(`${getCurrencyDisplay(invoiceState.currency, invoiceState.currencyDisplayType)} ${invoiceState.subtotal.toFixed(2)}`, amountColumnX, currentY, { align: "right" });
      currentY += 6;
      
      if (invoiceState.taxPercentage > 0) {
        pdf.text(`Tax (${invoiceState.taxPercentage}%):`, totalsX, currentY);
        pdf.text(`${getCurrencyDisplay(invoiceState.currency, invoiceState.currencyDisplayType)} ${invoiceState.taxAmount.toFixed(2)}`, amountColumnX, currentY, { align: "right" });
        currentY += 6;
      }
      
      if (invoiceState.discountPercentage > 0) {
        pdf.text(`Discount (${invoiceState.discountPercentage}%):`, totalsX, currentY);
        pdf.text(`${getCurrencyDisplay(invoiceState.currency, invoiceState.currencyDisplayType)} ${invoiceState.discountAmount.toFixed(2)}`, amountColumnX, currentY, { align: "right" });
        currentY += 6;
      }
      
      if (invoiceState.shipping > 0) {
        pdf.text("Shipping:", totalsX, currentY);
        pdf.text(`${getCurrencyDisplay(invoiceState.currency, invoiceState.currencyDisplayType)} ${invoiceState.shipping.toFixed(2)}`, amountColumnX, currentY, { align: "right" });
        currentY += 6;
      }
      
      pdf.setFont("helvetica", "bold");
      pdf.text("Total:", totalsX, currentY);
      pdf.text(`${getCurrencyDisplay(invoiceState.currency, invoiceState.currencyDisplayType)} ${invoiceState.total.toFixed(2)}`, amountColumnX, currentY, { align: "right" });
      currentY += 6;
      
      if (invoiceState.amountPaid > 0) {
        pdf.text("Amount Paid:", totalsX, currentY);
        pdf.text(`${getCurrencyDisplay(invoiceState.currency, invoiceState.currencyDisplayType)} ${invoiceState.amountPaid.toFixed(2)}`, amountColumnX, currentY, { align: "right" });
        currentY += 6;
        
        pdf.text("Balance Due:", totalsX, currentY);
        pdf.text(`${getCurrencyDisplay(invoiceState.currency, invoiceState.currencyDisplayType)} ${invoiceState.balanceDue.toFixed(2)}`, amountColumnX, currentY, { align: "right" });
        currentY += 6;
      }
      
      currentY += 15;
      
      // Add notes and terms
      if (invoiceState.notes) {
        pdf.setFont("helvetica", "bold");
        pdf.text("Notes:", margin, currentY);
        currentY += 6;
        
        pdf.setFont("helvetica", "normal");
        const notesLines = pdf.splitTextToSize(invoiceState.notes, contentWidth);
        notesLines.forEach((line: string) => {
          pdf.text(line, margin, currentY);
          currentY += 5;
        });
        currentY += 5;
      }
      
      if (invoiceState.terms) {
        pdf.setFont("helvetica", "bold");
        pdf.text("Terms:", margin, currentY);
        currentY += 6;
        
        pdf.setFont("helvetica", "normal");
        const termsLines = pdf.splitTextToSize(invoiceState.terms, contentWidth);
        termsLines.forEach((line: string) => {
          pdf.text(line, margin, currentY);
          currentY += 5;
        });
      }
      
      // Generate PDF as data URL
      const pdfDataUrl = pdf.output("datauristring");
      const fileName = `invoice_${invoiceState.invoiceNumber || 'unnamed'}_${new Date().toISOString().slice(0, 10)}.pdf`;
      
      const a = document.createElement("a");
      document.body.appendChild(a);
      a.href = pdfDataUrl;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(pdfDataUrl);

      return { pdfDataUrl, fileName };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to generate invoice PDF"
      );
    }
  }
);

export const InvoiceGeneratorSlice = createSlice({
  name: "invoiceGenerator",
  initialState,
  reducers: {
    logoChanged: (state, action: PayloadAction<string | null>) => {
      state.logoDataUrl = action.payload;
    },
    
    invoiceNumberChanged: (state, action: PayloadAction<string>) => {
      state.invoiceNumber = action.payload;
    },
    
    dateChanged: (state, action: PayloadAction<string>) => {
      state.date = action.payload;
    },
    
    currencyChanged: (state, action: PayloadAction<string>) => {
      state.currency = action.payload;
    },
    
    currencyDisplayTypeChanged: (state, action: PayloadAction<'symbol' | 'iso'>) => {
      state.currencyDisplayType = action.payload;
    },
    
    fromNameChanged: (state, action: PayloadAction<string>) => {
      state.fromName = action.payload;
    },
    
    fromAddressChanged: (state, action: PayloadAction<string>) => {
      state.fromAddress = action.payload;
    },
    
    fromCityChanged: (state, action: PayloadAction<string>) => {
      state.fromCity = action.payload;
    },
    
    fromStateChanged: (state, action: PayloadAction<string>) => {
      state.fromState = action.payload;
    },
    
    fromZipChanged: (state, action: PayloadAction<string>) => {
      state.fromZip = action.payload;
    },
    
    fromCountryChanged: (state, action: PayloadAction<string>) => {
      state.fromCountry = action.payload;
    },
    
    fromEmailChanged: (state, action: PayloadAction<string>) => {
      state.fromEmail = action.payload;
    },
    
    fromPhoneChanged: (state, action: PayloadAction<string>) => {
      state.fromPhone = action.payload;
    },
    
    billToNameChanged: (state, action: PayloadAction<string>) => {
      state.billToName = action.payload;
    },
    
    billToAddressChanged: (state, action: PayloadAction<string>) => {
      state.billToAddress = action.payload;
    },
    
    billToCityChanged: (state, action: PayloadAction<string>) => {
      state.billToCity = action.payload;
    },
    
    billToStateChanged: (state, action: PayloadAction<string>) => {
      state.billToState = action.payload;
    },
    
    billToZipChanged: (state, action: PayloadAction<string>) => {
      state.billToZip = action.payload;
    },
    
    billToCountryChanged: (state, action: PayloadAction<string>) => {
      state.billToCountry = action.payload;
    },
    
    billToEmailChanged: (state, action: PayloadAction<string>) => {
      state.billToEmail = action.payload;
    },
    
    billToPhoneChanged: (state, action: PayloadAction<string>) => {
      state.billToPhone = action.payload;
    },
    
    shipToNameChanged: (state, action: PayloadAction<string>) => {
      state.shipToName = action.payload;
    },
    
    shipToAddressChanged: (state, action: PayloadAction<string>) => {
      state.shipToAddress = action.payload;
    },
    
    shipToCityChanged: (state, action: PayloadAction<string>) => {
      state.shipToCity = action.payload;
    },
    
    shipToStateChanged: (state, action: PayloadAction<string>) => {
      state.shipToState = action.payload;
    },
    
    shipToZipChanged: (state, action: PayloadAction<string>) => {
      state.shipToZip = action.payload;
    },
    
    shipToCountryChanged: (state, action: PayloadAction<string>) => {
      state.shipToCountry = action.payload;
    },
    
    paymentTermsChanged: (state, action: PayloadAction<string>) => {
      state.paymentTerms = action.payload;
    },
    
    dueDateChanged: (state, action: PayloadAction<string>) => {
      state.dueDate = action.payload;
    },
    
    poNumberChanged: (state, action: PayloadAction<string>) => {
      state.poNumber = action.payload;
    },
    
    itemAdded: (state, action: PayloadAction<InvoiceItem>) => {
      state.items.push(action.payload);
      Object.assign(state, calculateTotals(state));
    },
    
    itemUpdated: (state, action: PayloadAction<{ id: string; updates: Partial<InvoiceItem> }>) => {
      const { id, updates } = action.payload;
      const itemIndex = state.items.findIndex(item => item.id === id);
      if (itemIndex !== -1) {
        state.items[itemIndex] = { ...state.items[itemIndex], ...updates };
        // Recalculate item amount
        const item = state.items[itemIndex];
        item.amount = item.quantity * item.rate;
        Object.assign(state, calculateTotals(state));
      }
    },
    
    itemRemoved: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.id !== action.payload);
      Object.assign(state, calculateTotals(state));
    },
    
    taxPercentageChanged: (state, action: PayloadAction<number>) => {
      state.taxPercentage = action.payload;
      Object.assign(state, calculateTotals(state));
    },
    
    discountPercentageChanged: (state, action: PayloadAction<number>) => {
      state.discountPercentage = action.payload;
      Object.assign(state, calculateTotals(state));
    },
    
    shippingChanged: (state, action: PayloadAction<number>) => {
      state.shipping = action.payload;
      Object.assign(state, calculateTotals(state));
    },
    
    amountPaidChanged: (state, action: PayloadAction<number>) => {
      state.amountPaid = action.payload;
      Object.assign(state, calculateTotals(state));
    },
    
    notesChanged: (state, action: PayloadAction<string>) => {
      state.notes = action.payload;
    },
    
    termsChanged: (state, action: PayloadAction<string>) => {
      state.terms = action.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    markAsDownloaded: (state) => {
      state.hasDownloaded = true;
    },
    
    resetGenerator: (state) => {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateInvoicePdfAsync.pending, (state) => {
        state.isGenerating = true;
        state.error = null;
      })
      .addCase(generateInvoicePdfAsync.fulfilled, (state, action) => {
        state.isGenerating = false;
        state.error = null;
      })
      .addCase(generateInvoicePdfAsync.rejected, (state, action) => {
        state.isGenerating = false;
        state.error = action.payload as string;
      });
  },
});

// Utility functions
export function validateLogoFile(file: File): string | null {
  if (!file.type.startsWith('image/')) {
    return "Please select a valid image file";
  }
  
  if (file.size > 5 * 1024 * 1024) { // 5MB limit
    return "Logo file size must be less than 5MB";
  }
  
  return null;
}

export function generateUniqueId(): string {
  return Math.random().toString(36).substr(2, 9);
}

export const {
  logoChanged,
  invoiceNumberChanged,
  dateChanged,
  currencyChanged,
  currencyDisplayTypeChanged,
  fromNameChanged,
  fromAddressChanged,
  fromCityChanged,
  fromStateChanged,
  fromZipChanged,
  fromCountryChanged,
  fromEmailChanged,
  fromPhoneChanged,
  billToNameChanged,
  billToAddressChanged,
  billToCityChanged,
  billToStateChanged,
  billToZipChanged,
  billToCountryChanged,
  billToEmailChanged,
  billToPhoneChanged,
  shipToNameChanged,
  shipToAddressChanged,
  shipToCityChanged,
  shipToStateChanged,
  shipToZipChanged,
  shipToCountryChanged,
  paymentTermsChanged,
  dueDateChanged,
  poNumberChanged,
  itemAdded,
  itemUpdated,
  itemRemoved,
  taxPercentageChanged,
  discountPercentageChanged,
  shippingChanged,
  amountPaidChanged,
  notesChanged,
  termsChanged,
  clearError,
  markAsDownloaded,
  resetGenerator,
} = InvoiceGeneratorSlice.actions;

export const selectState = (state: RootState) => state.invoiceGenerator;

export default InvoiceGeneratorSlice.reducer;
